/**
 * Camp art — every Camp's picture, drawn in code (no image files).
 *
 * A picture is an EMBLEM (the central item: a tent, a campfire, a canoe…) in a
 * SCENE (dawn meadow, night lake, snow peak…) in a PALETTE (the emblem's
 * colours). The backend picks a deterministic default from the slug
 * (lib/camps.ts `defaultArt`) and the creator can change it; the small details
 * — where the pines stand, which stars are out — are seeded from the slug too,
 * so two Camps with the same three choices still differ.
 *
 * Three crops of one drawing:
 *   <CampArt variant="card">    5:3, the directory card
 *   <CampArt variant="banner">  wide, the top of a Camp page (the same scene, sliced)
 *   <CampIcon size={24}>        round emblem: sky, horizon and the item, legible at 24 px
 *
 * The art is a picture, so it does not change with the site theme; a hairline
 * ring keeps it sitting cleanly on both the light and the dark page.
 */

import { useId } from 'react';

export const EMBLEMS = ['tent', 'campfire', 'lantern', 'canoe', 'compass', 'pennant', 'pine', 'mountain', 'moon', 'backpack', 'map', 'star'] as const;
export const SCENES = ['dawn_meadow', 'day_lake', 'dusk_forest', 'night_lake', 'snow_peak', 'desert_canyon', 'autumn_ridge', 'aurora_tundra'] as const;
export const PALETTES = ['ember', 'pine', 'glacier', 'dusk', 'sand', 'berry', 'moss', 'ocean'] as const;
export type Emblem = (typeof EMBLEMS)[number];
export type Scene = (typeof SCENES)[number];
export type Palette = (typeof PALETTES)[number];
export interface Art {
  emblem: string; scene: string; palette: string;
  /** The camp's real picture (`/api/camp-logo/<key>`): drawn instead of the emblem when set. */
  logo?: string | null;
}

export const EMBLEM_LABEL: Record<Emblem, string> = {
  tent: 'Tent', campfire: 'Campfire', lantern: 'Lantern', canoe: 'Canoe', compass: 'Compass', pennant: 'Pennants',
  pine: 'Pine', mountain: 'Summit', moon: 'Moon', backpack: 'Backpack', map: 'Trail map', star: 'North star',
};
export const SCENE_LABEL: Record<Scene, string> = {
  dawn_meadow: 'Dawn meadow', day_lake: 'Lake by day', dusk_forest: 'Dusk forest', night_lake: 'Night lake',
  snow_peak: 'Snow peak', desert_canyon: 'Desert canyon', autumn_ridge: 'Autumn ridge', aurora_tundra: 'Aurora tundra',
};
export const PALETTE_LABEL: Record<Palette, string> = {
  ember: 'Ember', pine: 'Pine', glacier: 'Glacier', dusk: 'Dusk', sand: 'Sand', berry: 'Berry', moss: 'Moss', ocean: 'Ocean',
};

interface Pal { main: string; light: string; dark: string }
const PAL: Record<Palette, Pal> = {
  ember: { main: '#f97316', light: '#fed7aa', dark: '#9a3412' },
  pine: { main: '#16a34a', light: '#bbf7d0', dark: '#14532d' },
  glacier: { main: '#0ea5e9', light: '#e0f2fe', dark: '#0c4a6e' },
  dusk: { main: '#8b5cf6', light: '#ddd6fe', dark: '#4c1d95' },
  sand: { main: '#d97706', light: '#fde68a', dark: '#78350f' },
  berry: { main: '#e11d48', light: '#fecdd3', dark: '#881337' },
  moss: { main: '#65a30d', light: '#d9f99d', dark: '#365314' },
  ocean: { main: '#2563eb', light: '#bfdbfe', dark: '#1e3a8a' },
};

interface SceneDef {
  sky: [string, string, string];
  far: string; mid: string; near: string; ground: string;
  night?: boolean; lake?: string; snow?: boolean;
  body: 'sun-low' | 'sun-high' | 'sun-big' | 'moon' | 'aurora' | 'sun-pale';
  bodyColor: string;
  mid_kind: 'hills' | 'pines' | 'mesas' | 'autumn' | 'peaks';
  /** The swatch the icon ring and fallbacks use. */
  tone: string;
}
const SC: Record<Scene, SceneDef> = {
  dawn_meadow: { sky: ['#7f9bd3', '#f4b6b0', '#ffd9a3'], far: '#b3a3d1', mid: '#8bb98f', near: '#6aa56c', ground: '#5a9660', body: 'sun-low', bodyColor: '#ffe0a3', mid_kind: 'hills', tone: '#f4b6b0' },
  day_lake: { sky: ['#3f9fe0', '#8fd0f5', '#dff3fc'], far: '#9cbfd9', mid: '#2f6f55', near: '#3f8a5e', ground: '#5d9f61', lake: '#3c93c9', body: 'sun-high', bodyColor: '#fff6c2', mid_kind: 'pines', tone: '#8fd0f5' },
  dusk_forest: { sky: ['#272457', '#a14f7c', '#f59a5c'], far: '#5e4475', mid: '#2e2547', near: '#211b36', ground: '#1a1529', body: 'sun-big', bodyColor: '#ffb874', mid_kind: 'pines', tone: '#a14f7c' },
  night_lake: { sky: ['#070d24', '#13214a', '#2a3d70'], far: '#1f2d55', mid: '#0f1a36', near: '#0b142b', ground: '#0a1226', lake: '#13254a', night: true, body: 'moon', bodyColor: '#f6f1cf', mid_kind: 'pines', tone: '#13214a' },
  snow_peak: { sky: ['#6fb1e3', '#b9def5', '#eef8fd'], far: '#a9c3dc', mid: '#7c9cbc', near: '#e9f2f9', ground: '#f4f8fc', snow: true, body: 'sun-pale', bodyColor: '#fffbe6', mid_kind: 'peaks', tone: '#b9def5' },
  desert_canyon: { sky: ['#e2784f', '#f3a96b', '#fde0a8'], far: '#d98b5f', mid: '#b8573a', near: '#9a4630', ground: '#d99a5f', body: 'sun-big', bodyColor: '#fff0c4', mid_kind: 'mesas', tone: '#f3a96b' },
  autumn_ridge: { sky: ['#6f90cf', '#e7b38f', '#f9d49c'], far: '#9b7393', mid: '#c4552e', near: '#8f3d26', ground: '#7a4a2c', body: 'sun-low', bodyColor: '#ffe3a6', mid_kind: 'autumn', tone: '#e7b38f' },
  aurora_tundra: { sky: ['#040a1c', '#0c2238', '#163a4a'], far: '#2a4560', mid: '#3d5a77', near: '#9fb6cf', ground: '#c7d6e6', night: true, snow: true, body: 'aurora', bodyColor: '#e9f6ff', mid_kind: 'peaks', tone: '#0c2238' },
};

const asScene = (s: string): Scene => ((SCENES as readonly string[]).includes(s) ? (s as Scene) : 'dawn_meadow');
const asEmblem = (s: string): Emblem => ((EMBLEMS as readonly string[]).includes(s) ? (s as Emblem) : 'tent');
const asPalette = (s: string): Palette => ((PALETTES as readonly string[]).includes(s) ? (s as Palette) : 'ember');

/** Same hash as the backend's default art, plus a small seeded RNG for details. */
function hash(s: string): number {
  let h = 2166136261;
  for (const ch of s) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619) >>> 0; }
  return h;
}
function rng(seed: number) {
  let a = seed || 1;
  return () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
export function defaultArt(slug: string): Art {
  const h = hash(slug);
  return { emblem: EMBLEMS[h % EMBLEMS.length], scene: SCENES[(h >>> 8) % SCENES.length], palette: PALETTES[(h >>> 16) % PALETTES.length] };
}

/** A pine silhouette: base centre (x, y), height h. */
function pine(x: number, y: number, h: number, fill: string, key: string | number) {
  const w = h * 0.42;
  return (
    <path key={key} fill={fill}
      d={`M${x} ${y - h} L${x + w * 0.45} ${y - h * 0.62} L${x + w * 0.28} ${y - h * 0.62} L${x + w * 0.75} ${y - h * 0.3} L${x + w * 0.5} ${y - h * 0.3} L${x + w} ${y} L${x - w} ${y} L${x - w * 0.5} ${y - h * 0.3} L${x - w * 0.75} ${y - h * 0.3} L${x - w * 0.28} ${y - h * 0.62} L${x - w * 0.45} ${y - h * 0.62} Z`} />
  );
}
function roundTree(x: number, y: number, h: number, fill: string, trunk: string, key: string | number) {
  return (
    <g key={key}>
      <rect x={x - 1.4} y={y - h * 0.35} width={2.8} height={h * 0.35} fill={trunk} />
      <ellipse cx={x} cy={y - h * 0.6} rx={h * 0.32} ry={h * 0.42} fill={fill} />
    </g>
  );
}

/** A mountain range path across the width, seeded. */
function ridge(r: () => number, base: number, amp: number, peaks: number, sharp = true, W = 400): string {
  let d = `M0 240 L0 ${base}`;
  const step = W / peaks;
  for (let i = 0; i < peaks; i++) {
    const x0 = i * step;
    const px = x0 + step * (0.3 + r() * 0.4);
    const py = base - amp * (0.55 + r() * 0.45);
    if (sharp) d += ` L${px.toFixed(1)} ${py.toFixed(1)} L${(x0 + step).toFixed(1)} ${(base - amp * r() * 0.25).toFixed(1)}`;
    else d += ` Q${px.toFixed(1)} ${py.toFixed(1)} ${(x0 + step).toFixed(1)} ${(base - amp * r() * 0.2).toFixed(1)}`;
  }
  return d + ` L${W} 240 Z`;
}

/* ── Emblems: drawn in a 100×100 box, ground at y=0, centred on x=0. ───────── */

function EmblemShape({ emblem, p, id, small = false }: { emblem: Emblem; p: Pal; id: string; small?: boolean }) {
  const ink = '#1b1a24';
  const sw = small ? 5 : 2.2;
  switch (emblem) {
    case 'tent':
      return (
        <g strokeLinejoin="round" strokeLinecap="round">
          {!small && <path d="M-46 0 L-58 6 M46 0 L58 6" stroke={p.light} strokeWidth="1.2" opacity=".7" />}
          <path d="M0 -78 L48 0 L-48 0 Z" fill={p.main} stroke={p.dark} strokeWidth={sw} />
          <path d="M0 -78 L48 0 L22 0 Z" fill={p.dark} opacity=".35" />
          <path d="M0 -40 L15 0 L-15 0 Z" fill={ink} opacity=".85" />
          <path d="M0 -40 L0 0" stroke={p.light} strokeWidth="1.2" opacity=".6" />
          <path d="M0 -78 L0 -94" stroke={p.dark} strokeWidth={sw} />
          <path d="M0 -94 L16 -89 L0 -84 Z" fill={p.light} />
        </g>
      );
    case 'campfire':
      return (
        <g>
          {!small && <ellipse cx="0" cy="-26" rx="46" ry="40" fill={`url(#${id}-glow)`} />}
          <path d="M-34 -4 L30 -16" stroke="#6b3f22" strokeWidth={small ? 12 : 9} strokeLinecap="round" />
          <path d="M34 -4 L-30 -16" stroke="#7c4a28" strokeWidth={small ? 12 : 9} strokeLinecap="round" />
          <path d="M0 -84 C14 -62 30 -48 24 -26 C20 -12 10 -8 0 -8 C-10 -8 -22 -12 -24 -26 C-28 -44 -12 -52 -8 -64 C-2 -56 2 -58 0 -84 Z" fill={p.main} />
          <path d="M2 -60 C12 -46 18 -36 14 -24 C11 -15 6 -13 0 -13 C-7 -13 -13 -17 -13 -26 C-13 -36 -5 -40 -3 -48 C1 -44 3 -50 2 -60 Z" fill="#fbbf24" />
          <path d="M1 -38 C6 -31 8 -26 6 -21 C4 -17 -4 -17 -6 -21 C-7 -26 -2 -29 1 -38 Z" fill="#fff7d6" />
          {!small && [[-18, -92, 2.2], [14, -100, 1.8], [26, -84, 1.4], [-6, -110, 1.6], [-28, -76, 1.2], [6, -122, 1.1]].map(([x, y, r], i) => (
            <circle key={i} cx={x} cy={y} r={r} fill={i % 2 ? '#fde68a' : p.light} opacity={0.9 - i * 0.1} />
          ))}
        </g>
      );
    case 'lantern':
      return (
        <g>
          {!small && <circle cx="0" cy="-44" r="46" fill={`url(#${id}-glow)`} />}
          <path d="M0 -100 L0 -88" stroke={ink} strokeWidth={sw} />
          <path d="M-9 -100 A9 9 0 0 1 9 -100" fill="none" stroke={ink} strokeWidth={sw} />
          <path d="M-18 -78 L18 -78 L12 -88 L-12 -88 Z" fill={p.dark} />
          <rect x="-20" y="-78" width="40" height="52" rx="7" fill="#fff2c4" stroke={p.dark} strokeWidth={small ? 6 : 3} />
          <rect x="-12" y="-72" width="24" height="40" rx="5" fill="#fcd34d" />
          <path d="M0 -62 C5 -55 6 -50 3 -46 C1 -44 -2 -44 -4 -46 C-6 -50 -3 -54 0 -62 Z" fill={p.main} />
          <path d="M-22 -26 L22 -26 L16 -16 L-16 -16 Z" fill={p.dark} />
          <path d="M-8 -16 L8 -16 L6 0 L-6 0 Z" fill={p.dark} opacity={small ? 0 : 0.6} />
        </g>
      );
    case 'canoe':
      return (
        <g transform="translate(0 -10)">
          <path d="M-58 -22 C-36 -2 36 -2 58 -22 C40 -12 -40 -12 -58 -22 Z" fill={p.dark} />
          <path d="M-58 -22 C-38 -8 38 -8 58 -22 C44 -4 18 4 0 4 C-18 4 -44 -4 -58 -22 Z" fill={p.main} stroke={p.dark} strokeWidth={small ? 4 : 1.6} />
          <path d="M-50 -14 C-20 -6 20 -6 50 -14" stroke={p.light} strokeWidth="2" fill="none" opacity=".75" />
          <path d="M-8 -78 L22 -6" stroke="#8a5a32" strokeWidth={small ? 7 : 3.5} strokeLinecap="round" />
          <path d="M20 -12 C26 -6 30 6 24 10 C18 8 14 -2 16 -10 Z" fill="#a8703f" />
          {!small && <path d="M-70 6 C-40 10 40 10 70 6" stroke="#ffffff" strokeWidth="1.4" opacity=".35" fill="none" />}
        </g>
      );
    case 'compass':
      return (
        <g>
          <circle cx="0" cy="-46" r="40" fill={p.dark} />
          <circle cx="0" cy="-46" r="34" fill="#fbf7ec" />
          {!small && [0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
            <path key={a} d="M0 -76 L0 -71" transform={`rotate(${a} 0 -46)`} stroke={p.dark} strokeWidth={a % 90 ? 1.2 : 2.4} />
          ))}
          <path d="M0 -76 L8 -46 L0 -46 Z" fill={p.main} transform="rotate(28 0 -46)" />
          <path d="M0 -76 L-8 -46 L0 -46 Z" fill={p.dark} transform="rotate(28 0 -46)" />
          <path d="M0 -16 L8 -46 L0 -46 Z" fill="#cbd5e1" transform="rotate(28 0 -46)" />
          <path d="M0 -16 L-8 -46 L0 -46 Z" fill="#94a3b8" transform="rotate(28 0 -46)" />
          <circle cx="0" cy="-46" r="4" fill={p.dark} />
          <circle cx="0" cy="-90" r="6" fill="none" stroke={p.dark} strokeWidth={small ? 5 : 3} />
          {!small && <text x="0" y="-61" textAnchor="middle" fontSize="9" fontWeight="700" fill={p.dark} fontFamily="system-ui, sans-serif">N</text>}
        </g>
      );
    case 'pennant':
      return (
        <g strokeLinecap="round">
          <path d="M-30 0 L-30 -96" stroke="#5b4636" strokeWidth={small ? 7 : 3.5} />
          <path d="M-30 -96 C-6 -92 14 -84 40 -80 C14 -72 -6 -66 -30 -60 Z" fill={p.main} />
          <path d="M-30 -96 C-6 -92 14 -84 40 -80 C14 -82 -8 -84 -30 -84 Z" fill={p.light} opacity=".35" />
          {!small && <>
            <path d="M14 0 L14 -62" stroke="#5b4636" strokeWidth="3" />
            <path d="M14 -62 C26 -60 36 -56 48 -54 C36 -49 26 -46 14 -44 Z" fill={p.light} />
            <path d="M-46 -28 C-30 -20 -14 -20 2 -24 C18 -28 30 -26 50 -20" stroke={p.dark} strokeWidth="1.2" fill="none" />
            {[-40, -26, -12, 2, 16, 30, 44].map((x, i) => (
              <path key={x} d={`M${x} ${-27 + Math.sin(i) * 2} l5 0 l-2.5 8 z`} fill={i % 2 ? p.main : p.light} />
            ))}
          </>}
        </g>
      );
    case 'pine':
      return (
        <g>
          <rect x="-5" y="-14" width="10" height="14" fill="#5b3a22" />
          <path d="M0 -104 L24 -70 L13 -70 L36 -40 L22 -40 L46 -12 L-46 -12 L-22 -40 L-36 -40 L-13 -70 L-24 -70 Z" fill={p.main} stroke={p.dark} strokeWidth={small ? 4 : 2} strokeLinejoin="round" />
          <path d="M0 -104 L24 -70 L13 -70 L36 -40 L22 -40 L46 -12 L0 -12 Z" fill={p.dark} opacity=".3" />
          {!small && <path d="M0 -112 l3 6 6 .5 -4.6 4 1.4 6 -5.8 -3.2 -5.8 3.2 1.4 -6 -4.6 -4 6 -.5 z" fill="#fde68a" />}
        </g>
      );
    case 'mountain':
      return (
        <g strokeLinejoin="round">
          <path d="M-56 0 L-8 -76 L10 -50 L22 -64 L60 0 Z" fill={p.dark} />
          <path d="M-8 -76 L10 -50 L22 -64 L60 0 L18 0 Z" fill="#000" opacity=".18" />
          <path d="M-8 -76 L4 -58 L-2 -54 L-10 -60 L-18 -50 L-22 -54 Z" fill="#f8fafc" />
          <path d="M-8 -76 L-8 -100" stroke="#3f3f46" strokeWidth={small ? 5 : 2.5} />
          <path d="M-8 -100 L12 -94 L-8 -88 Z" fill={p.main} />
        </g>
      );
    case 'moon':
      return (
        <g>
          {!small && <circle cx="0" cy="-58" r="50" fill={`url(#${id}-glow)`} />}
          <path d="M14 -96 A40 40 0 1 0 22 -20 A32 32 0 1 1 14 -96 Z" fill="#fdf6d8" stroke={p.light} strokeWidth={small ? 3 : 1} />
          <circle cx="-14" cy="-70" r="4" fill="#ece3b8" />
          <circle cx="-6" cy="-40" r="3" fill="#ece3b8" />
          <path d="M34 -84 l2 5 5 2 -5 2 -2 5 -2 -5 -5 -2 5 -2 z" fill={p.light} />
          {!small && <path d="M40 -46 l1.4 3.6 3.6 1.4 -3.6 1.4 -1.4 3.6 -1.4 -3.6 -3.6 -1.4 3.6 -1.4 z" fill={p.light} />}
        </g>
      );
    case 'backpack':
      return (
        <g strokeLinejoin="round">
          <rect x="-30" y="-92" width="60" height="14" rx="7" fill={p.light} stroke={p.dark} strokeWidth={small ? 4 : 1.6} />
          <path d="M-26 -78 C-26 -88 26 -88 26 -78 L28 -6 C28 -2 24 0 20 0 L-20 0 C-24 0 -28 -2 -28 -6 Z" fill={p.main} stroke={p.dark} strokeWidth={small ? 5 : 2} />
          <path d="M-26 -60 C-10 -52 10 -52 26 -60 L26 -48 C10 -40 -10 -40 -26 -48 Z" fill={p.dark} opacity=".55" />
          <rect x="-16" y="-36" width="32" height="24" rx="5" fill={p.dark} opacity=".45" />
          <rect x="-4" y="-56" width="8" height="10" rx="2" fill="#e5e7eb" />
        </g>
      );
    case 'map':
      return (
        <g strokeLinejoin="round">
          <path d="M-52 -76 L-18 -84 L18 -76 L52 -84 L52 -8 L18 0 L-18 -8 L-52 0 Z" fill="#f5ecd2" stroke={p.dark} strokeWidth={small ? 5 : 2} />
          <path d="M-18 -84 L-18 -8 M18 -76 L18 0" stroke={p.dark} strokeWidth="1.4" opacity=".4" />
          <path d="M-18 -84 L18 -76 L18 0 L-18 -8 Z" fill="#000" opacity=".06" />
          <path d="M-40 -18 C-28 -40 -6 -26 2 -46 C8 -60 24 -54 34 -64" stroke={p.main} strokeWidth={small ? 6 : 2.4} strokeDasharray={small ? 'none' : '5 4'} fill="none" strokeLinecap="round" />
          <path d="M28 -70 L40 -58 M40 -70 L28 -58" stroke={p.dark} strokeWidth={small ? 6 : 3} strokeLinecap="round" />
          {!small && <path d="M-44 -60 l6 -10 6 10 z M-36 -60 l5 -8 5 8 z" fill={p.dark} opacity=".5" />}
        </g>
      );
    case 'star':
    default:
      return (
        <g>
          {!small && <circle cx="0" cy="-54" r="52" fill={`url(#${id}-glow)`} />}
          <path d="M0 -108 C4 -70 16 -58 46 -54 C16 -50 4 -38 0 0 C-4 -38 -16 -50 -46 -54 C-16 -58 -4 -70 0 -108 Z" fill={p.light} stroke={p.main} strokeWidth={small ? 4 : 1.6} strokeLinejoin="round" />
          <path d="M0 -84 C2 -64 8 -58 22 -54 C8 -50 2 -44 0 -24 C-2 -44 -8 -50 -22 -54 C-8 -58 -2 -64 0 -84 Z" fill="#ffffff" />
          {!small && <path d="M-34 -90 l1.4 3.6 3.6 1.4 -3.6 1.4 -1.4 3.6 -1.4 -3.6 -3.6 -1.4 3.6 -1.4 z M36 -24 l1 3 3 1 -3 1 -1 3 -1 -3 -3 -1 3 -1 z" fill={p.light} />}
        </g>
      );
  }
}

/** Emblems that stand ON the ground versus the ones that hang in the sky. */
const SKY_EMBLEMS = new Set<Emblem>(['moon', 'star']);

function SceneLayers({ s, seed, id, W = 400, ex = 200 }: { s: SceneDef; seed: number; id: string; W?: number; ex?: number }) {
  // The generator is made HERE, per render: a shared one would advance between
  // StrictMode's two renders and the server and browser would draw different skies.
  const r = rng(seed);
  const k = W / 400;
  const sy = W > 400 ? 18 : 0;   // banners crop the top of the sky; the sun or moon sits lower there
  const stars = s.night ? Array.from({ length: Math.round(46 * k) }, () => [r() * W, r() * 130, 0.4 + r() * 1.1, 0.35 + r() * 0.65]) : [];
  // The sun or moon keeps clear of the emblem.
  let bodyX = 60 + r() * (W - 120);
  if (Math.abs(bodyX - ex) < 70) bodyX = ex > W / 2 ? ex - 120 - r() * 80 : ex + 120 + r() * 80;
  const farPath = ridge(r, s.mid_kind === 'peaks' ? 150 : 158, s.mid_kind === 'peaks' ? 92 : 58, Math.round((4 + Math.floor(r() * 3)) * (1 + (k - 1) * 0.55)), s.mid_kind !== 'hills', W);
  const midPath = ridge(r, 178, 34, Math.round((5 + Math.floor(r() * 3)) * (1 + (k - 1) * 0.55)), s.mid_kind === 'peaks', W);
  const groundTop = s.lake ? 206 : 196;
  return (
    <>
      <rect width={W} height="240" fill={`url(#${id}-sky)`} />
      {stars.map(([x, y, rad, o], i) => <circle key={i} cx={x} cy={y} r={rad} fill="#fff" opacity={o} />)}
      {s.body === 'aurora' && (
        <g opacity=".85">
          <path transform={`scale(${k} 1)`} d={`M-20 ${70 + r() * 20} C80 ${20 + r() * 30} 160 ${110 + r() * 20} 240 ${50 + r() * 20} S380 ${30 + r() * 30} 430 ${70 + r() * 20}`} stroke="#34f5b4" strokeWidth="26" fill="none" opacity=".35" style={{ filter: 'blur(6px)' }} />
          <path transform={`scale(${k} 1)`} d={`M-20 ${95 + r() * 10} C90 ${50 + r() * 20} 170 ${125 + r() * 10} 250 ${75 + r() * 10} S390 ${60 + r() * 20} 430 ${95 + r() * 10}`} stroke="#5eead4" strokeWidth="12" fill="none" opacity=".45" style={{ filter: 'blur(3px)' }} />
          <path transform={`scale(${k} 1)`} d={`M-20 ${55 + r() * 10} C70 ${25 + r() * 10} 180 ${80 + r() * 10} 260 ${35 + r() * 10} S380 ${20 + r() * 10} 430 ${50 + r() * 10}`} stroke="#a78bfa" strokeWidth="10" fill="none" opacity=".35" style={{ filter: 'blur(4px)' }} />
        </g>
      )}
      {s.body === 'moon' && (
        <g>
          <circle cx={bodyX} cy={54 + sy} r="34" fill={`url(#${id}-halo)`} />
          <circle cx={bodyX} cy={54 + sy} r="15" fill={s.bodyColor} />
          <circle cx={bodyX + 7} cy={49 + sy} r="13" fill={s.sky[0]} opacity=".92" />
        </g>
      )}
      {(s.body === 'sun-low' || s.body === 'sun-big' || s.body === 'sun-high' || s.body === 'sun-pale') && (
        <g>
          <circle cx={bodyX} cy={s.body === 'sun-high' || s.body === 'sun-pale' ? 52 + sy : 150} r={s.body === 'sun-big' ? 70 : 52} fill={`url(#${id}-halo)`} />
          <circle cx={bodyX} cy={s.body === 'sun-high' || s.body === 'sun-pale' ? 52 + sy : 150} r={s.body === 'sun-big' ? 30 : s.body === 'sun-high' ? 16 : 22} fill={s.bodyColor} opacity=".95" />
        </g>
      )}
      <path d={farPath} fill={s.far} opacity={s.night ? 1 : 0.9} />
      {s.snow && <path d={farPath} fill={`url(#${id}-cap)`} />}
      {s.mid_kind === 'mesas' ? (
        <g fill={s.mid}>
          {Array.from({ length: Math.max(3, Math.round(3 * k)) }, (_, i) => {
            const x = 20 + i * 130 + r() * 40;
            const w = 70 + r() * 50;
            const top = 120 + r() * 30;
            return <path key={i} d={`M${x} 200 L${x + 8} ${top} L${x + w - 8} ${top} L${x + w} 200 Z`} />;
          })}
          <rect x="0" y="176" width={W} height="30" fill={s.near} />
        </g>
      ) : (
        <path d={midPath} fill={s.mid} />
      )}
      {s.snow && <path d={midPath} fill="#fff" opacity=".18" />}
      {s.lake && (
        <g>
          <rect x="0" y="186" width={W} height="30" fill={s.lake} />
          <rect x="0" y="186" width={W} height="30" fill={`url(#${id}-water)`} />
          {Array.from({ length: Math.round(7 * k) }, (_, i) => <rect key={i} x={r() * (W - 40)} y={190 + r() * 22} width={10 + r() * 30} height="1.2" fill="#fff" opacity={s.night ? 0.25 : 0.4} />)}
          {s.body === 'moon' && <rect x={bodyX - 6} y="188" width="12" height="26" fill={s.bodyColor} opacity=".22" />}
        </g>
      )}
      {(s.mid_kind === 'pines' || s.mid_kind === 'peaks') && (
        <g>
          {Array.from({ length: Math.round(16 * k) }, (_, i) => {
            const x = (i / Math.round(16 * k)) * (W + 20) - 10 + r() * 18;
            if (x > ex - 60 && x < ex + 60) return null;
            return pine(x, s.lake ? 190 : 190, 22 + r() * 30, s.mid_kind === 'peaks' ? (s.night ? '#1d3247' : '#3f5f73') : s.near, i);
          })}
        </g>
      )}
      {s.mid_kind === 'autumn' && (
        <g>
          {Array.from({ length: Math.round(14 * k) }, (_, i) => {
            const x = (i / Math.round(14 * k)) * (W + 20) - 10 + r() * 16;
            if (x > ex - 55 && x < ex + 55) return null;
            const fills = ['#d9622b', '#b8402f', '#e8a33d', '#c9772f'];
            return roundTree(x, 194, 26 + r() * 20, fills[Math.floor(r() * fills.length)], '#4a2a17', i);
          })}
        </g>
      )}
      {s.mid_kind === 'hills' && (
        <g>
          <path d={`M0 196 Q${W / 4} ${172 + r() * 10} ${W / 2} 186 T${W} ${178 + r() * 8} L${W} 240 L0 240 Z`} fill={s.near} />
          {Array.from({ length: Math.round(22 * k) }, (_, i) => (
            <circle key={i} cx={r() * W} cy={200 + r() * 34} r={1.2 + r() * 1.4} fill={['#fde68a', '#fbcfe8', '#ffffff', '#fca5a5'][i % 4]} opacity=".9" />
          ))}
        </g>
      )}
      <path d={`M0 ${groundTop} Q${W / 2} ${groundTop - 8} ${W} ${groundTop} L${W} 240 L0 240 Z`} fill={s.ground} />
      {s.snow && <path d={`M0 ${groundTop + 4} Q${W / 2} ${groundTop - 4} ${W} ${groundTop + 4}`} stroke="#fff" strokeWidth="2" opacity=".5" fill="none" />}
    </>
  );
}

function Defs({ id, s, p }: { id: string; s: SceneDef; p: Pal }) {
  return (
    <defs>
      <linearGradient id={`${id}-sky`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={s.sky[0]} />
        <stop offset=".55" stopColor={s.sky[1]} />
        <stop offset="1" stopColor={s.sky[2]} />
      </linearGradient>
      <radialGradient id={`${id}-halo`}>
        <stop offset="0" stopColor={s.bodyColor} stopOpacity=".7" />
        <stop offset="1" stopColor={s.bodyColor} stopOpacity="0" />
      </radialGradient>
      <radialGradient id={`${id}-glow`}>
        <stop offset="0" stopColor={p.light} stopOpacity=".85" />
        <stop offset=".5" stopColor={p.main} stopOpacity=".25" />
        <stop offset="1" stopColor={p.main} stopOpacity="0" />
      </radialGradient>
      <radialGradient id={`${id}-pool`}>
        <stop offset="0" stopColor="#000" stopOpacity=".35" />
        <stop offset="1" stopColor="#000" stopOpacity="0" />
      </radialGradient>
      <linearGradient id={`${id}-water`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#fff" stopOpacity=".18" />
        <stop offset="1" stopColor="#000" stopOpacity=".2" />
      </linearGradient>
      <linearGradient id={`${id}-cap`} gradientUnits="userSpaceOnUse" x1="0" y1="60" x2="0" y2="130">
        <stop offset="0" stopColor="#fff" stopOpacity=".95" />
        <stop offset=".45" stopColor="#fff" stopOpacity=".55" />
        <stop offset="1" stopColor="#fff" stopOpacity="0" />
      </linearGradient>
      <linearGradient id={`${id}-shade`} x1="0" y1="0" x2="0" y2="1">
        <stop offset=".6" stopColor="#000" stopOpacity="0" />
        <stop offset="1" stopColor="#000" stopOpacity=".28" />
      </linearGradient>
    </defs>
  );
}

const cleanId = (raw: string) => 'ca' + raw.replace(/[^a-zA-Z0-9_-]/g, '');

/** Only our own logo URLs are drawn (they come from the API's `art.logo`). */
const cleanLogo = (v: unknown): string | null => (typeof v === 'string' && /^\/api\/camp-logo\/[0-9a-f]{32}$/.test(v) ? v : null);

/**
 * The camp's logo on a cream plaque with a palette rim, standing in the scene
 * where the emblem would — brand marks (often a coloured square) and transparent
 * icons both read on it, over any scene.
 */
function LogoPlaque({ href, cx, cy, size, p, id }: { href: string; cx: number; cy: number; size: number; p: Pal; id: string }) {
  const r = size * 0.2, x = cx - size / 2, y = cy - size / 2, pad = size * 0.1;
  return (
    <g>
      <defs>
        <clipPath id={`${id}-logo`}><rect x={x + pad} y={y + pad} width={size - pad * 2} height={size - pad * 2} rx={r * 0.6} /></clipPath>
      </defs>
      <ellipse cx={cx} cy={y + size + 10} rx={size * 0.46} ry="7" fill="#000" opacity=".22" />
      <rect x={x - 4} y={y - 4} width={size + 8} height={size + 8} rx={r + 3} fill={p.dark} opacity=".55" />
      <rect x={x} y={y} width={size} height={size} rx={r} fill="#fffaf0" stroke={p.main} strokeWidth="3" />
      <image href={href} x={x + pad} y={y + pad} width={size - pad * 2} height={size - pad * 2} preserveAspectRatio="xMidYMid meet" clipPath={`url(#${id}-logo)`} />
    </g>
  );
}

/** The full picture: a card (5:3) or a wide banner (the same scene, sliced). */
export function CampArt({ art, slug, variant = 'card', className = '', title }: {
  art?: Partial<Art> | null; slug: string; variant?: 'card' | 'banner'; className?: string; title?: string;
}) {
  const id = cleanId(useId());
  const d = defaultArt(slug);
  const emblem = asEmblem(art?.emblem ?? d.emblem);
  const s = SC[asScene(art?.scene ?? d.scene)];
  const p = PAL[asPalette(art?.palette ?? d.palette)];
  const seed = hash(slug + '|' + (art?.scene ?? ''));
  const inSky = SKY_EMBLEMS.has(emblem);
  const logo = cleanLogo(art?.logo);
  // Banner: a natively wide scene (not a zoomed crop of the card), the emblem
  // on the right where it stays in frame (xMax) and clear of the Camp's name.
  const banner = variant === 'banner';
  const W = banner ? 1200 : 400;
  const ex = banner ? W - 130 : 200;
  return (
    <svg viewBox={banner ? `0 30 ${W} 210` : '0 0 400 240'} preserveAspectRatio={banner ? 'xMaxYMax slice' : 'xMidYMid slice'} className={className}
         role={title ? 'img' : undefined} aria-label={title} aria-hidden={title ? undefined : true}>
      <Defs id={id} s={s} p={p} />
      <SceneLayers s={s} seed={seed} id={id} W={W} ex={ex} />
      {logo ? (
        <LogoPlaque href={logo} cx={ex} cy={banner ? 150 : 128} size={banner ? 128 : 118} p={p} id={id} />
      ) : (
        <>
          {!inSky && <ellipse cx={ex} cy={213} rx="62" ry="8" fill={`url(#${id}-pool)`} />}
          <g transform={inSky ? `translate(${ex} ${banner ? 172 : 140}) scale(0.95)` : `translate(${ex} 212) scale(0.98)`}>
            <EmblemShape emblem={emblem} p={p} id={id} />
          </g>
        </>
      )}
      <rect width={W} height="240" fill={`url(#${id}-shade)`} />
    </svg>
  );
}

/** The round emblem: sky, a horizon in the palette, and the item — built to read at 20–28 px. */
export function CampIcon({ art, slug, size = 24, className = '' }: { art?: Partial<Art> | null; slug: string; size?: number; className?: string }) {
  const id = cleanId(useId());
  const d = defaultArt(slug);
  const emblem = asEmblem(art?.emblem ?? d.emblem);
  const s = SC[asScene(art?.scene ?? d.scene)];
  const p = PAL[asPalette(art?.palette ?? d.palette)];
  const small = size < 40;
  const inSky = SKY_EMBLEMS.has(emblem);
  const logo = cleanLogo(art?.logo);
  if (logo) {
    return (
      <span aria-hidden className={'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-white ' + className}
            style={{ width: size, height: size, boxShadow: `inset 0 0 0 ${size >= 40 ? 2 : 1}px ${p.main}55` }}>
        {/* eslint-disable-next-line @next/next/no-img-element -- a same-origin, immutable 256 px logo; next/image adds nothing here */}
        <img src={logo} alt="" width={size} height={size} loading="lazy" decoding="async" draggable={false}
             style={{ width: '82%', height: '82%', objectFit: 'contain' }} />
      </span>
    );
  }
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} className={'shrink-0 rounded-full ' + className} aria-hidden>
      <defs>
        <linearGradient id={`${id}-sky`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={s.sky[0]} />
          <stop offset="1" stopColor={s.sky[2]} />
        </linearGradient>
        <clipPath id={`${id}-clip`}><circle cx="32" cy="32" r="32" /></clipPath>
        <radialGradient id={`${id}-glow`}>
          <stop offset="0" stopColor={p.light} stopOpacity=".8" />
          <stop offset="1" stopColor={p.main} stopOpacity="0" />
        </radialGradient>
      </defs>
      <g clipPath={`url(#${id}-clip)`}>
        <rect width="64" height="64" fill={`url(#${id}-sky)`} />
        {s.night && <><circle cx="14" cy="14" r=".9" fill="#fff" /><circle cx="50" cy="11" r=".7" fill="#fff" /><circle cx="44" cy="22" r=".6" fill="#fff" opacity=".7" /></>}
        <path d="M0 46 L14 34 L24 41 L38 28 L52 39 L64 33 L64 64 L0 64 Z" fill={s.far} opacity=".9" />
        <path d="M0 52 Q32 44 64 52 L64 64 L0 64 Z" fill={s.ground} />
        <g transform={inSky ? 'translate(32 52) scale(0.42)' : 'translate(32 54) scale(0.42)'}>
          <EmblemShape emblem={emblem} p={p} id={id} small={small} />
        </g>
      </g>
      <circle cx="32" cy="32" r="31.25" fill="none" stroke="#000" strokeOpacity=".14" strokeWidth="1.5" />
    </svg>
  );
}

/** For pickers: the tone swatch of a scene and the main colour of a palette. */
export const sceneTone = (s: string) => SC[asScene(s)].sky;
export const paletteMain = (p: string) => PAL[asPalette(p)];
